import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, timeout: 60000 });

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('cub_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let onUnauthorized = null;
export const setUnauthorizedHandler = (cb) => { onUnauthorized = cb; };

http.interceptors.response.use(
  (r) => r,
  (err) => {
    // Non auto-logout se l'errore proviene dagli endpoint di autenticazione:
    // altrimenti su credenziali sbagliate faremmo un reload che impedisce
    // di mostrare il messaggio di errore all'utente.
    const url = err?.config?.url || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/login-json');
    if (err?.response?.status === 401 && !isAuthEndpoint) {
      if (onUnauthorized) onUnauthorized();
    }
    return Promise.reject(err);
  }
);

export const api = {
  login: (email, password, remember_me = false) => {
    const form = new URLSearchParams();
    form.append('username', email); form.append('password', password);
    if (remember_me) form.append('scope', 'remember_me');
    return http.post(`/auth/login${remember_me ? '?remember_me=true' : ''}`, form, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }).then(r => r.data);
  },
  register: (payload) => http.post('/auth/register', payload).then(r => r.data),
  me: () => http.get('/auth/me').then(r => r.data),
  changePassword: (current_password, new_password) => http.post('/auth/change-password', { current_password, new_password }).then(r => r.data),
  forgotPassword: (email) => http.post('/auth/forgot-password', { email }).then(r => r.data),
  resetPassword: (token, new_password) => http.post('/auth/reset-password', { token, new_password }).then(r => r.data),
  listLoginAttempts: (params = {}) => http.get('/admin/login-attempts', { params }).then(r => r.data),
  listPdfErrors: (params = {}) => http.get('/admin/pdf-errors', { params }).then(r => r.data),
  downloadPdfErrorsCsv: async (params = {}) => {
    const r = await http.get('/admin/pdf-errors/export.csv', { params, responseType: 'blob' });
    return r.data;
  },
  testCloudinaryPdf: (url) => http.post('/admin/pdf-errors/test-cloudinary', { url }).then(r => r.data),
  migrateProductsToCloudinary: (params = {}) => http.post('/admin/products/migrate-to-cloudinary', null, { params }).then(r => r.data),

  getOptions: () => http.get('/meta/options').then(r => r.data),
  getStats: () => http.get('/stats').then(r => r.data),

  listCompanies: () => http.get('/companies').then(r => r.data),
  getCompany: (id) => http.get(`/companies/${id}`).then(r => r.data),
  createCompany: (data) => http.post('/companies', data).then(r => r.data),
  updateCompany: (id, data) => http.put(`/companies/${id}`, data).then(r => r.data),
  deleteCompany: (id) => http.delete(`/companies/${id}`).then(r => r.data),

  listProducts: (params={}) => http.get('/products', { params }).then(r => r.data),
  createProduct: (data) => http.post('/products', data).then(r => r.data),
  updateProduct: (id, data) => http.put(`/products/${id}`, data).then(r => r.data),
  deleteProduct: (id) => http.delete(`/products/${id}`).then(r => r.data),

  listContracts: (params={}) => http.get('/contracts', { params }).then(r => r.data),
  contractsAggregations: (params = {}) => http.get('/contracts/aggregations', { params }).then(r => r.data),
  getContract: (id) => http.get(`/contracts/${id}`).then(r => r.data),
  createContract: (data) => http.post('/contracts', data).then(r => r.data),
  updateContract: (id, data) => http.put(`/contracts/${id}`, data).then(r => r.data),
  deleteContract: (id) => http.delete(`/contracts/${id}`).then(r => r.data),
  signContract: (id, payload) => http.post(`/contracts/${id}/sign`, payload).then(r => r.data),
  // === Customers (anagrafica) ===
  listCustomers: (params = {}) => http.get('/customers', { params }).then(r => r.data),
  getCustomer: (id) => http.get(`/customers/${id}`).then(r => r.data),
  createCustomer: (data) => http.post('/customers', data).then(r => r.data),
  updateCustomer: (id, data) => http.put(`/customers/${id}`, data).then(r => r.data),
  deleteCustomer: (id) => http.delete(`/customers/${id}`).then(r => r.data),
  getCustomerContracts: (id) => http.get(`/customers/${id}/contracts`).then(r => r.data),
  listQuotes: (params = {}) => http.get('/quotes', { params }).then(r => r.data),
  saveQuoteDraft: (payload) => http.post('/quotes/save-draft', payload).then(r => r.data),
  quotesAggregations: (params = {}) => http.get('/quotes/aggregations', { params }).then(r => r.data),

  // ===== INSTALLAZIONI (Fase 27) =====
  listInstallations: (params = {}) => http.get('/installations', { params }).then(r => r.data),
  installationsAggregations: () => http.get('/installations/aggregations').then(r => r.data),
  getInstallation: (id) => http.get(`/installations/${id}`).then(r => r.data),
  updateInstallation: (id, body) => http.patch(`/installations/${id}`, body).then(r => r.data),
  assignInstallation: (id, body) => http.post(`/installations/${id}/assign`, body).then(r => r.data),
  uploadChecklistFile: (id, itemKey, body) => http.post(`/installations/${id}/checklist/${itemKey}/upload`, body).then(r => r.data),
  verifyChecklistItem: (id, itemKey, body) => http.post(`/installations/${id}/checklist/${itemKey}/verify`, body).then(r => r.data),
  listInstallers: () => http.get('/users/installers').then(r => r.data),
  adminListInstallers: () => http.get('/admin/installers').then(r => r.data),

  // ===== TARIFFARIO COMPENSI (Fase 27b) =====
  listCompensationRates: () => http.get('/compensation-rates').then(r => r.data),
  createCompensationRate: (body) => http.post('/compensation-rates', body).then(r => r.data),
  updateCompensationRate: (id, body) => http.patch(`/compensation-rates/${id}`, body).then(r => r.data),
  deleteCompensationRate: (id) => http.delete(`/compensation-rates/${id}`).then(r => r.data),
  recomputeInstallationCompensation: (id) => http.post(`/installations/${id}/recompute-compensation`).then(r => r.data),

  // ===== ONBOARDING PARTNER (Fase 28) =====
  createOnboarding: (body) => http.post('/admin/onboarding', body).then(r => r.data),
  listOnboarding: (params = {}) => http.get('/admin/onboarding', { params }).then(r => r.data),
  getOnboarding: (id) => http.get(`/admin/onboarding/${id}`).then(r => r.data),
  approveOnboarding: (id) => http.post(`/admin/onboarding/${id}/approve`).then(r => r.data),
  rejectOnboarding: (id, reason) => http.post(`/admin/onboarding/${id}/reject`, { reject_reason: reason }).then(r => r.data),
  resendOnboarding: (id) => http.post(`/admin/onboarding/${id}/resend`).then(r => r.data),
  // Endpoint pubblici (senza token JWT) — richiedono un axios separato
  publicGetOnboarding: (token) => http.get(`/public/onboarding/${token}`, { headers: { Authorization: '' } }).then(r => r.data),
  publicUploadOnboarding: (token, body) => http.post(`/public/onboarding/${token}/upload`, body, { headers: { Authorization: '' } }).then(r => r.data),
  publicSubmitOnboarding: (token, body) => http.post(`/public/onboarding/${token}/submit`, body, { headers: { Authorization: '' } }).then(r => r.data),
  getQuote: (id) => http.get(`/quotes/${id}`).then(r => r.data),
  updateQuote: (id, body) => http.put(`/quotes/${id}`, body).then(r => r.data),
  deleteQuote: (id) => http.delete(`/quotes/${id}`).then(r => r.data),
  markQuoteConverted: (id, contract_id = null) => http.post(`/quotes/${id}/mark-converted`, contract_id ? { contract_id } : {}).then(r => r.data),
  assignQuote: (id, user_id) => http.post(`/quotes/${id}/assign`, { user_id }).then(r => r.data),
  assignCustomer: (id, user_id, company_id = null) => http.post(`/customers/${id}/assign`, company_id ? { user_id, company_id } : { user_id }).then(r => r.data),
  getAreaManagerStats: () => http.get('/stats/area-manager').then(r => r.data),
  uploadCustomerDoc: (id, payload) => http.post(`/customers/${id}/documents`, payload).then(r => r.data),
  deleteCustomerDoc: (id, docId) => http.delete(`/customers/${id}/documents/${docId}`).then(r => r.data),
  getCustomerDocCategories: () => http.get('/customers/doc-categories').then(r => r.data),
  migrateCustomers: () => http.post('/customers/migrate-from-contracts').then(r => r.data),

  sendContractOtp: (payload) => http.post('/contracts/otp/send', payload).then(r => r.data),
  verifyContractOtp: (payload) => http.post('/contracts/otp/verify', payload).then(r => r.data),
  getOtpLogs: (params = {}) => http.get('/contracts/otp/logs', { params }).then(r => r.data),
  exportOtpLogs: (params = {}) => http.get('/contracts/otp/logs/export', { params, responseType: 'blob' }).then(r => r.data),
  openContractPdf: async (id, mode='filled') => {
    const r = await http.get(`/contracts/${id}/pdf`, { params: { mode }, responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },
  openCompanyBlankPdf: async (companyId) => {
    const r = await http.get(`/companies/${companyId}/blank-pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },
  // Helper: quando responseType è 'blob', gli errori JSON del backend arrivano come Blob.
  // Questa funzione converte l'errore in un errore "normale" con response.data.detail leggibile.
  _blobErrorMiddleware: async (err) => {
    try {
      const blob = err?.response?.data;
      if (blob && typeof blob.text === 'function') {
        const txt = await blob.text();
        try {
          const j = JSON.parse(txt);
          err.response.data = j;
        } catch {
          err.response.data = { detail: txt.slice(0, 500) };
        }
      }
    } catch { /* ignore */ }
    throw err;
  },
  openPreventivoPdf: async (payload) => {
    try {
      const r = await http.post('/preventivo/pdf', payload, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      // ritorna eventuali warning (per toast in UI)
      let warnings = null;
      try {
        const warn = r.headers?.['x-pdf-warnings'] || r.headers?.get?.('x-pdf-warnings');
        if (warn) {
          const encoded = r.headers?.['x-pdf-failed-datasheets'] || r.headers?.get?.('x-pdf-failed-datasheets');
          let failed = [];
          if (encoded) { try { failed = JSON.parse(decodeURIComponent(escape(atob(encoded)))); } catch { failed = []; } }
          warnings = { message: warn, failed_datasheets: failed };
        }
      } catch { /* ignore */ }
      return warnings;
    } catch (e) {
      await api._blobErrorMiddleware(e);
    }
  },
  previewBusinessPlan: (payload) => http.post('/preventivo/business-plan', payload).then(r => r.data),
  fetchPreventivoPdfUrl: async (payload) => {
    try {
      const r = await http.post('/preventivo/pdf', payload, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      // Estrai warning schede tecniche non scaricate dagli headers
      let warnings = null;
      try {
        const warn = r.headers?.['x-pdf-warnings'] || r.headers?.get?.('x-pdf-warnings');
        if (warn) {
          const encoded = r.headers?.['x-pdf-failed-datasheets'] || r.headers?.get?.('x-pdf-failed-datasheets');
          let failed = [];
          if (encoded) {
            try { failed = JSON.parse(decodeURIComponent(escape(atob(encoded)))); }
            catch { failed = []; }
          }
          warnings = { message: warn, failed_datasheets: failed };
        }
      } catch { /* ignore */ }
      // ritorna oggetto se ci sono warning, altrimenti stringa url (backward compat)
      return warnings ? { url, warnings } : url;
    } catch (e) {
      await api._blobErrorMiddleware(e);
    }
  },
  fetchContractPdfUrl: async (id, mode = 'filled') => {
    try {
      const r = await http.get(`/contracts/${id}/pdf`, { params: { mode }, responseType: 'blob' });
      return URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    } catch (e) {
      await api._blobErrorMiddleware(e);
    }
  },
  sendPreventivoEmail: (payload) => http.post('/preventivo/send-email', payload).then(r => r.data),
  sendContractEmail: (id, data) => http.post(`/contracts/${id}/send-email`, data).then(r => r.data),
  listEmailLogs: (params = {}) => http.get('/email-logs', { params }).then(r => r.data),
  previewEmail: (payload) => http.post('/emails/preview', payload).then(r => r.data),
  setAgentVisibility: (companyId, productId, visible) => http.post(`/companies/${companyId}/agent-visibility`, { product_id: productId, visible }).then(r => r.data),
  exportListinoXlsx: async (companyId, companyName = 'listino') => {
    const r = await http.get(`/companies/${companyId}/products/export-xlsx`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `listino_${companyName.replace(/\s+/g, '_')}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  },
  importListinoXlsx: (companyId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post(`/companies/${companyId}/products/import-xlsx`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  // Tickets
  createTicket: (payload) => http.post('/tickets', payload).then(r => r.data),
  getTicketAssignableUsers: () => http.get('/tickets/assignable-users').then(r => r.data),
  listTickets: (params = {}) => http.get('/tickets', { params }).then(r => r.data),
  getTicket: (id) => http.get(`/tickets/${id}`).then(r => r.data),
  addTicketMessage: (id, body, attachments = []) => http.post(`/tickets/${id}/messages`, { body, attachments }).then(r => r.data),
  setTicketStatus: (id, status) => http.put(`/tickets/${id}/status`, { status }).then(r => r.data),
  assignTicket: (id, userId) => http.put(`/tickets/${id}/assign`, { user_id: userId }).then(r => r.data),
  getTicketsUnread: () => http.get('/tickets/unread-count').then(r => r.data),
  extractPalette: (image_data_url) => http.post('/utils/extract-palette', { image_data_url }).then(r => r.data),
  pvgisEstimate: (payload) => http.post('/utils/pvgis-estimate', payload).then(r => r.data),
  seedEnergy4people: () => http.post('/admin/seed/energy4people').then(r => r.data),
  bulkEnableCompaniesOnProducts: (payload) => http.post('/products/bulk/enable-companies', payload).then(r => r.data),

  listUsers: (params={}) => http.get('/users', { params }).then(r => r.data),
  createUser: (data) => http.post('/users', data).then(r => r.data),
  updateUser: (id, data) => http.patch(`/users/${id}`, data).then(r => r.data),
  deleteUser: (id) => http.delete(`/users/${id}`).then(r => r.data),

  listNotifications: (params={}) => http.get('/notifications', { params }).then(r => r.data),
  unreadCount: () => http.get('/notifications/unread-count').then(r => r.data),
  markNotificationRead: (id) => http.patch(`/notifications/${id}/read`).then(r => r.data),
  markAllNotificationsRead: () => http.patch('/notifications/mark-all-read').then(r => r.data),
};

export function formatCurrency(value) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(value || 0));
}
export function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}
export function formatRelative(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return 'ora';
  if (diff < 3600) return `${Math.floor(diff/60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff/3600)} h fa`;
  if (diff < 604800) return `${Math.floor(diff/86400)} g fa`;
  return formatDate(iso);
}
export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error);
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(file);
  });
}
